// Custom orthogonal "fillet" SVG path builder, ported from the design
// handoff's `buildOrtho()` (`NodeShowcase.jsx`). React Flow's
// `getSmoothStepPath` produces a midpoint elbow and internally clamps
// the arc to `min(leg/2, leg/2, borderRadius)` — so anchoring the bend
// near the source (which the design does) collapses the arc back to a
// sharp corner. The design solves this with a hand-built path: short
// fixed stub off each port, two big arcs, long vertical run between
// them. We replicate that here.

// Geometry knobs lifted from the design's handoff. `FILLET` is the
// target arc radius; `STUB` is the minimum horizontal stub length off
// the source/target port, sized so the arc always fits (`FILLET + 4`,
// clamped to ≥ 28). Both kept in sync with the design's constants so a
// future handoff refresh is a one-line swap here.
const FILLET = 24;
const STUB = Math.max(28, FILLET + 4);

/**
 * Build a fillet-style orthogonal SVG path between two points,
 * assuming the source emits to the right and the target receives from
 * the left (the standard L→R canvas layout).
 *
 * Shape:
 *   1. Short horizontal stub (STUB px) out of the source port.
 *   2. Rounded arc turning from horizontal to vertical.
 *   3. Long vertical run between source-y and target-y.
 *   4. Rounded arc turning from vertical back to horizontal.
 *   5. Long horizontal approach into the target port.
 *
 * Returns a fallback polyline (no arcs) when the source / target
 * geometry leaves no room for a full-sized fillet.
 */
export function buildOrthoPath(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
): string {
  const dy = targetY - sourceY;
  const dirY = dy >= 0 ? 1 : -1;
  // mx = x-coordinate of the vertical run, anchored STUB px to the
  // right of the source port. The TARGET stub is the implicit residue
  // between mx and target.x — typically long, which is why the wire
  // reads as "leaves source, drops, then sweeps in".
  const mx = sourceX + STUB;

  // Clamp the fillet to whatever room the legs allow. The design's
  // formula also takes `legV / 2` so the two arcs never kiss when
  // source and target are vertically close.
  const legA = Math.abs(mx - sourceX);
  const legB = Math.abs(targetX - mx);
  const legV = Math.abs(dy);
  const R = Math.max(0, Math.min(FILLET, legA, legB, legV / 2));

  // Degenerate (aligned or near-aligned ports) — emit a polyline so we
  // never produce an invalid `A` command with R ≈ 0.
  if (R <= 0.5) {
    return `M ${sourceX} ${sourceY} L ${mx} ${sourceY} L ${mx} ${targetY} L ${targetX} ${targetY}`;
  }

  const c1x = mx - Math.sign(mx - sourceX) * R;
  const c1yEnd = sourceY + dirY * R;
  const c2yStart = targetY - dirY * R;
  const c2x = mx + Math.sign(targetX - mx) * R;
  // Sweep flags determine the arc's curl direction. Derived geometric-
  // ally from `dirY` and the relative x-positions; kept verbatim from
  // the design so the math is auditable against the handoff source.
  const sweepA = Math.sign(mx - sourceX) === dirY ? 1 : 0;
  const sweepB = Math.sign(targetX - mx) === dirY ? 0 : 1;

  return [
    `M ${sourceX} ${sourceY}`,
    `L ${c1x} ${sourceY}`,
    `A ${R} ${R} 0 0 ${sweepA} ${mx} ${c1yEnd}`,
    `L ${mx} ${c2yStart}`,
    `A ${R} ${R} 0 0 ${sweepB} ${c2x} ${targetY}`,
    `L ${targetX} ${targetY}`,
  ].join(' ');
}

// Exported so callers can decide whether the geometry is safe to use
// before computing the path. Returns `false` for "backward" wires
// (target left of source) and for ports that aren't far enough apart
// to fit two stubs — both cases produce visually wrong shapes because
// the path would have to U-turn through the source node.
export function isForwardOrthoLayout(
  sourceX: number,
  targetX: number,
): boolean {
  return targetX >= sourceX + 2 * STUB;
}
